/**
 * API клієнт для doc.material_receipt_for_processing — Прийняття чужої сировини.
 * Permission: production.material_receipt.manage.
 */
import { api } from './client';
import type { Page } from './catalogs';
import type { CancellableState } from './cancellation';

export interface MaterialReceiptItemIn {
  line_number: number;
  nomenclature_id: string;
  quantity: string;
  uom_id?: string | null;
  declared_value?: string | null;
  account_code?: string | null;  // Счет учета позабаланс (деф.022)
  /** ч.687: склад рядка (NULL = шапка). */
  warehouse_id?: string | null;
}

export interface MaterialReceiptItem extends MaterialReceiptItemIn {
  id: string;
}

export interface MaterialReceiptListItem {
  id: string;
  organization_id: string;
  document_number: string;
  document_date: string;
  customer_contractor_id: string;
  destination_warehouse_id: string | null;
  total_declared_value: string | null;
  posted: boolean;
  deletion_mark: boolean;
  external_source?: string | null;
  // Block 5.3c:
  state?: CancellableState;
}

export interface MaterialReceipt extends MaterialReceiptListItem {
  contract_id: string | null;
  posted_at: string | null;
  comment: string | null;
  project_id: string | null;
  items: MaterialReceiptItem[];
  // Block 5.3c:
  cancelled_at?: string | null;
  cancellation_reason?: string | null;
}

export interface MaterialReceiptCreate {
  organization_id: string;
  document_number?: string | null;
  document_date: string;
  customer_contractor_id: string;
  contract_id?: string | null;
  destination_warehouse_id: string | null;
  total_declared_value?: string | null;
  comment?: string | null;
  project_id?: string | null;
  items?: MaterialReceiptItemIn[];
}

export type MaterialReceiptUpdate = Partial<Omit<MaterialReceiptCreate, 'organization_id'>>;

export const materialReceiptsForProcessing = {
  list: (params?: {
    organization_id?: string; date_from?: string; date_to?: string; sort_by?: string; sort_dir?: 'asc' | 'desc';
    customer_contractor_id?: string;
    posted?: boolean;
    limit?: number;
    offset?: number;
  }) =>
    api.get<Page<MaterialReceiptListItem>>('/material-receipts-for-processing', { params })
      .then((r) => r.data),
  get: (id: string) =>
    api.get<MaterialReceipt>(`/material-receipts-for-processing/${id}`).then((r) => r.data),
  create: (payload: MaterialReceiptCreate) =>
    api.post<MaterialReceipt>('/material-receipts-for-processing', payload).then((r) => r.data),
  update: (id: string, payload: MaterialReceiptUpdate) =>
    api.patch<MaterialReceipt>(`/material-receipts-for-processing/${id}`, payload).then((r) => r.data),
  remove: (id: string) =>
    api.delete(`/material-receipts-for-processing/${id}`).then(() => undefined),
  post: (id: string) =>
    api.post<MaterialReceipt>(`/material-receipts-for-processing/${id}/post`).then((r) => r.data),
  unpost: (id: string) =>
    api.post<MaterialReceipt>(`/material-receipts-for-processing/${id}/unpost`).then((r) => r.data),
  cancel: (id: string, reason: string) =>
    api.post(`/material-receipts-for-processing/${id}/cancel`, { cancellation_reason: reason }).then((r) => r.data),
};
